import type { CarouselSlide } from '@/lib/base/carousel';
import { Carousel } from '@/lib/base/carousel';
import { ExpandableImage } from '@/lib/base/image';

// Screens captured from Salesforce's External Client App flow. Salesforce revises
// this UI periodically — if the steps drift, re-shoot these five frames (redact any
// real contact email) and keep the file names stable so this list needn't change.
interface SetupShot {
  id: string;
  src: string;
  alt: string;
  caption: string;
}

const SETUP_SHOTS: SetupShot[] = [
  {
    id: 'app-manager',
    src: '/assets/salesforce/setup/01-app-manager.png',
    alt: 'Salesforce External Client App Manager with the New External Client App button highlighted',
    caption:
      'In Setup, open Apps → External Client Apps → External Client App Manager, then click New External Client App.',
  },
  {
    id: 'basic-info',
    src: '/assets/salesforce/setup/02-basic-info.png',
    alt: 'The Basic Information section of the new External Client App form',
    caption:
      'Under Basic Information, name the app and leave Distribution State as Local.',
  },
  {
    id: 'oauth-scopes',
    src: '/assets/salesforce/setup/03-oauth-callback-scopes.png',
    alt: 'OAuth settings showing the callback URL and the three selected OAuth scopes',
    caption:
      'Enable OAuth, paste the Callback URL shown below the steps, and add the three scopes: api, refresh_token, id.',
  },
  {
    id: 'security-pkce',
    src: '/assets/salesforce/setup/04-security-pkce.png',
    alt: 'Security settings with the two require-secret checkboxes ticked and PKCE required',
    caption:
      'In Security, tick both “Require secret…” boxes. PKCE is already required – leave it on.',
  },
  {
    id: 'consumer-key-secret',
    src: '/assets/salesforce/setup/05-consumer-key-secret.png',
    alt: 'The saved app’s Settings tab with the Consumer Key and Secret button',
    caption:
      'Save, then open Consumer Key and Secret to copy both values into the fields below.',
  },
];

const SLIDES: CarouselSlide[] = SETUP_SHOTS.map(shot => ({
  id: shot.id,
  content: <ExpandableImage src={shot.src} alt={shot.alt} />,
  caption: shot.caption,
}));

/**
 * A visual walkthrough of creating a Salesforce External Client App, shown
 * alongside the text steps on the credentials setup form.
 */
export function SalesforceSetupCarousel() {
  return (
    <Carousel
      slides={SLIDES}
      aria-label='Salesforce External Client App setup steps'
    />
  );
}
